import { Component, OnDestroy, OnInit, computed, signal } from "@angular/core";

import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { Router } from "@angular/router";
import { firstValueFrom, Subscription } from "rxjs";
import { debounceTime, filter } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import { ChequeSyncService } from "../../services/cheque-sync.service";
import { DataService } from "../../services/data.service";
import { DomainService } from "../../services/domain.service";
import { OrderService } from "../../services/order.service";
import { PayAtTablePaymentService } from "../../services/pay-at-table-payment.service";
import { TableSocketService } from "../../services/table-socket.service";
import { PaymentOptionResult } from "../../shared/payment-options-sheet/payment-options-sheet";
import {
  SplitBillOption,
  SplitBillOptionsSheet,
} from "../split-bill-options.sheet/split-bill-options.sheet";
import { PaymentMethod } from "../../models/cart.model";
import { PayAtTableOrder, PayAtTableCheque, PayAtTableFee, PayAtTableResponse, PayAtTableNiPayload, PayAtTableOrderItemPayload } from "../../models/pay-at-table.model";
import { AppliedPromo, AppliedVoucher } from "../../models/voucher.model";
import { Table } from "../../models/config.model";
import { VoucherMode } from "../../shared/enums";
import { isChequeEvent, SocketEventType } from "../../models/websocket.model";
import { ToastService } from "../../services/toast.service";

type OrderLine = {
  qty: number;
  name: string;
  price: number;
};

type PaymentMode = "SPLIT" | "FULL";
@Component({
  selector: "app-payment-page",
  templateUrl: "./payments.html",
  styleUrls: ["./payments.scss"],
  standalone: false
})
export class PaymentPageComponent implements OnInit, OnDestroy {
  public lines = signal<OrderLine[]>([]);
  private orders = signal<PayAtTableOrder[]>([]);
  private chequePaymentMode = signal<string | null>(null);
  private cheque = signal<PayAtTableCheque | null>(null);
  table: Table | null = null;
  public chequeCode = computed(() => this.cheque()?.cheque_code || "");
  public appliedVoucher = signal<AppliedVoucher | null>(null);
  public VoucherMode = VoucherMode;
  tipAmount = signal<number>(0);

  public mode = signal<PaymentMode>("SPLIT");

  public serviceFee = signal<number>(0);
  public fees = signal<PayAtTableFee[]>([]);
  public paymentMethods: PaymentMethod[] = [];
  public paymentType: PaymentMethod | undefined;

  public voucher = signal<string | null>(null);
  public hideVoucher = signal(false);

  public subtotal = computed(() => {
    const chequeSubtotal = Number(this.cheque()?.subtotal ?? 0);
    if (chequeSubtotal > 0) {
      return chequeSubtotal;
    }
    return this.orders().reduce(
      (sum, order) => sum + (order.total_price_without_discount || 0),
      0
    );
  });

  public discount = computed(() => Number(this.cheque()?.discount ?? 0));
  // An automatic promotion, separate from a redeemed voucher (this.appliedVoucher) -
  // both can discount the same cheque and each needs its own line, mirroring the cart.
  public appliedPromo = computed<AppliedPromo | null>(() => this.cheque()?.promo ?? null);
  public remainingAmount = computed(() => {
    const remaining = Number(this.cheque()?.remaining_amount_without_discount ?? 0);
    if (remaining > 0) {
      return remaining;
    }
    return Number(this.orderTotal() ?? 0);
  });

  public orderTotal = computed(() => {
    const cheque = this.cheque();
    const chequeTotal = Number(
      cheque?.amount_to_pay ?? cheque?.remaining_amount ?? 0
    );
    return Math.max(0, chequeTotal);
  });

  orderTotalWithTip = computed(() => this.orderTotal() + this.tipAmount())

  public isOrderFree = computed(() => this.orderTotalWithTip() <= 0);
  public remainingBalance = computed(() => {
    const voucherAmount = Number(this.appliedVoucher()?.amount ?? 0);
    if (voucherAmount) {
      return voucherAmount - this.remainingAmount();
    }
    return 0;
  });

  public topOrderTotal = computed(() => {
    const chequeAmount = Number(this.cheque()?.original_amount ?? 0);
    if (chequeAmount > 0) {
      return chequeAmount;
    }
    return this.subtotal();
  });
  public splitApplied = signal(false);
  public isLoading = signal(false);

  // PAT payment lock: another phone at the table holds it.
  public lockedByOther = computed(() => this.chequeSyncService.lockedByOther());

  // Read (and cleared) only when actually navigating back, not on load — the same localStorage
  // entry must survive the page being reconstructed by a gateway reload/redirect round-trip.
  // Falls back to the menu when it's missing — a fixed destination sidesteps
  // history.back()/gateway-redirect quirks entirely instead of trying to out-guess them.
  public goToMenu = (): void => {
    const originUrl = this.payAtTablePaymentService.consumeOrigin();
    if (originUrl) {
      this.router.navigateByUrl(originUrl);
      return;
    }
    this.router.navigate(['/menu-home']);
  };

  private chequeUpdateSubscription: Subscription;
  private chequeClosedSubscription: Subscription;

  constructor(
    private bottomSheet: MatBottomSheet,
    private router: Router,
    private dataService: DataService,
    private payAtTablePaymentService: PayAtTablePaymentService,
    private chequeSyncService: ChequeSyncService,
    private domainService: DomainService,
    private toastService: ToastService,
    private orderService: OrderService,
    private tableSocketService: TableSocketService,
    private translateService: TranslateService
  ) { }

  ngOnInit(): void {
    this.table = this.dataService.table();
    this.initPaymentMethods();
    this.applyPostpaidCheckoutThenLoadOrders();
    this.chequeSyncService.enterPaymentFlow();

    this.chequeUpdateSubscription = this.tableSocketService.message$.pipe(
      filter((event) => isChequeEvent(event) && (!this.cheque()?.id || event.cheque_id === this.cheque()?.id)),
      debounceTime(300),
    ).subscribe(() => this.applyPostpaidCheckoutThenLoadOrders(this.voucher()));

    this.chequeClosedSubscription = this.tableSocketService.message$.pipe(
      filter((event) => event.type === SocketEventType.ChequeClosed && event.cheque_id === this.cheque()?.id),
    ).subscribe(() => this.clearChequeState());
  }

  ngOnDestroy(): void {
    this.chequeUpdateSubscription?.unsubscribe();
    this.chequeClosedSubscription?.unsubscribe();
    this.chequeSyncService.leavePaymentFlow();
  }

  private clearChequeState(): void {
    this.orders.set([]);
    this.lines.set([]);
    this.fees.set([]);
    this.chequePaymentMode.set(null);
    this.cheque.set(null);
    this.appliedVoucher.set(null);
    this.serviceFee.set(0);
    this.voucher.set(null);
    this.hideVoucher.set(false);
    this.dataService.voucherName.set(null);
  }

  scrollToBottom(): void {
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
  }

  async onSplitBillClick(): Promise<void> {
    if (this.guardLockedByOther()) return;
    const ref = this.bottomSheet.open(SplitBillOptionsSheet, {
      panelClass: "split-options-sheet",
      backdropClass: "sheet-backdrop",
      data: {
        paymentMode: this.chequePaymentMode(),
      },
    });

    const option = (await firstValueFrom(
      ref.afterDismissed()
    )) as SplitBillOption;
    if (!option) return;
    this.splitApplied.set(true);

    const typeMap: Record<SplitBillOption, string> = {
      EVENLY: "evenly",
      CUSTOM_AMOUNT: "custom",
      PAY_FOR_ITEMS: "items",
    };

    await this.router.navigate(["pay-at-table/split"], {
      queryParams: { type: typeMap[option] },
    });
  }


  public applyPostpaidCheckoutThenLoadOrders(voucherName: string | null = null): void {
    // Blocked while locked; the initial voucherless load still runs so viewers see the bill.
    if (voucherName && this.guardLockedByOther()) {
      return;
    }
    this.isLoading.set(true);
    this.payAtTablePaymentService
      .applyPostpaidCheckoutThenLoadOrders(voucherName ?? undefined)
      .subscribe({
        next: (res) => {
          this.isLoading.set(false);
          if (!res) {
            this.voucher.set(null);
            this.hideVoucher.set(false);
            this.dataService.voucherName.set(null);
            return;
          }
          this.voucher.set(voucherName);
          this.hideVoucher.set(!!voucherName);
          this.dataService.voucherName.set(voucherName);
          this.applyPayAtTableResponse(res);
        },
        error: (err) => {
          this.isLoading.set(false);
          this.voucher.set(null);
          this.hideVoucher.set(false);
          this.dataService.voucherName.set(null);
          const message =
            err?.error?.message;
          this.toastService.error(this.translateService.instant(message));
          if (voucherName) this.applyPostpaidCheckoutThenLoadOrders();
        },
      });
  }

  public onVoucherApplied(data: { voucher: string | null }): void {
    this.applyPostpaidCheckoutThenLoadOrders(data.voucher);
  }

  onTipApplied(data: { tip_amount: number }): void {
    this.tipAmount.set(data.tip_amount);
  }

  private applyPayAtTableResponse(res: PayAtTableResponse): void {
    this.orders.set(res.orders);
    this.fees.set(res.orders?.[0]?.fees ?? []);
    if (res.orders?.length) {
      this.lines.set(this.buildLines(res.orders));
    } else {
      this.lines.set([]);
    }
    this.chequePaymentMode.set(res.cheque?.payment_mode ?? null);
    this.cheque.set(res.cheque ?? null);
    this.appliedVoucher.set(res.voucher ?? null);

    const chequeFees = Number(res.cheque?.fees ?? 0);
    this.serviceFee.set(chequeFees);
  }

  private guardLockedByOther(): boolean {
    if (!this.chequeSyncService.lockedByOther()) {
      return false;
    }
    this.toastService.warning(this.translateService.instant("PAY_AT_TABLE.SOMEONE_ELSE_PAYING"));
    return true;
  }

  private buildLines(orders: PayAtTableOrder[]): OrderLine[] {
    const lines: OrderLine[] = [];

    orders.forEach((order) => {
      order.items.forEach((item) => {
        lines.push({
          qty: item.quantity,
          name: item.item,
          price: item.total_price_without_discount,
        });
      });
    });

    return lines;
  }

  onPaymentOptionSelected(selected: PaymentOptionResult): void {
    if (this.guardLockedByOther()) return;
    if (!selected) return;

    this.paymentType = selected;
    this.processPayment();
  }

  private initPaymentMethods(): void {
    const methods = this.dataService.paymentMethods();
    if (!methods?.length) return;

    this.paymentMethods = this.payAtTablePaymentService.filterOutUnavailableApplePay(methods);
    this.paymentType = this.paymentMethods[0];

    this.payAtTablePaymentService.filterOutUnavailableGooglePay(this.paymentMethods).then((filtered) => {
      if (!filtered) return;
      this.paymentMethods = filtered;
      if (this.paymentType?.key === "googlepay") {
        this.paymentType = this.paymentMethods[0];
      }
    });
  }

  processPayment(): void {
    this.isLoading.set(true);

    if (!this.paymentType && !this.isOrderFree()) {
      this.isLoading.set(false);
      return;
    }

    const payload = this.buildPayAtTablePaymentPayload();
    if (!payload) {
      this.isLoading.set(false);
      return;
    }

    const currency =
      this.dataService.restaurant().currency ??
      this.orders()?.[0]?.currency ??
      "AED";
    const amount = Number(this.orderTotalWithTip() ?? 0);

    const voucherName = this.voucher() ?? undefined;
    const isWallet =
      this.paymentType?.key === "applepay" || this.paymentType?.key === "googlepay";

    if (this.isOrderFree()) {
      this.chequeSyncService.markPaymentInFlight(true);
      this.payAtTablePaymentService
        .confirmPostpaidCheckoutWithPayAtTablePayload(payload, voucherName)
        .subscribe((res) => {
          if (!res) {
            this.chequeSyncService.markPaymentInFlight(false);
            this.isLoading.set(false);
            return;
          }
          if (res?.transaction_id) {
            this.orderService.setLastTransaction(res.transaction_id).subscribe({
              next: () => this.router.navigate(["/bill-paid"]),
              error: () => this.router.navigate(["/bill-paid"]),
            });
          } else {
            this.router.navigate(["/bill-paid"]);
          }
        });
      return;
    }

    if (isWallet) {
      this.chequeSyncService.markPaymentInFlight(true);
      this.payAtTablePaymentService.startPayment(
        payload,
        this.paymentType,
        amount,
        currency,
        (value) => this.isLoading.set(value),
        voucherName,
      );
      return;
    }

    this.isLoading.set(true);
    this.chequeSyncService.markPaymentInFlight(true);
    this.payAtTablePaymentService
      .confirmPostpaidCheckoutWithPayAtTablePayload(payload, voucherName)
      .subscribe((res) => {
        if (!res) {
          this.chequeSyncService.markPaymentInFlight(false);
          this.isLoading.set(false);
          return;
        }
        this.payAtTablePaymentService.startPayment(
          payload,
          this.paymentType,
          amount,
          currency,
          (value) => this.isLoading.set(value)
        );
      });
  }

  private buildPayAtTablePaymentPayload(): PayAtTableNiPayload | null {
    const tableId = this.dataService.table()?.id;
    if (!tableId) {
      return null;
    }

    const envelope = {
      table_id: tableId,
      subdomain: this.domainService.getSubdomain(),
      user_id: this.dataService.getUserId(),
      tip_amount: this.tipAmount() ?? 0,
    };

    const cheque = this.cheque();
    if (cheque?.payment_mode === "custom_amount") {
      return {
        ...envelope,
        payment_mode: "custom_amount",
        payment_details: {
          amount: Number(cheque.remaining_amount_without_discount ?? 0),
        },
      };
    }

    if (cheque?.payment_mode === "split") {
      const totalPeople = Number(cheque.max_payers ?? 1);
      const paidPeople = Number(cheque.current_payers ?? 1);
      return {
        ...envelope,
        payment_mode: "split",
        payment_details: {
          total_people: totalPeople,
          paying_for: Math.max(1, totalPeople - paidPeople),
        },
      };
    }

    if (cheque?.payment_mode === "item_selection" || cheque?.items?.length) {
      return {
        ...envelope,
        payment_mode: "item_selection",
        payment_details: {
          order_items: this.toOrderItemPayloads(cheque?.items ?? [], (item) => item.item_id),
        },
      };
    }

    const orderItems = this.orders().flatMap((order) => order.items);
    return {
      ...envelope,
      payment_mode: "item_selection",
      payment_details: {
        order_items: this.toOrderItemPayloads(orderItems, (item) => item.order_item_id),
      },
    };
  }

  private toOrderItemPayloads<T extends { quantity: number }>(
    items: T[],
    getId: (item: T) => string
  ): PayAtTableOrderItemPayload[] {
    return items
      .map((item) => ({ item_id: getId(item), quantity: item.quantity }))
      .filter((item) => item.item_id && item.quantity > 0);
  }

}
